import Button from 'devextreme-react/button';
import { formatLocalRange } from '../../../../utils/timezone';
import { durationModeLabel, inferDurationMode } from '../timeOffDuration';
import { timeOffDetailRowLabel, timeOffDetailSection } from '../timeOffEntryPopupStyles';
import type { TimeOffEntryViewModel } from '../timeOffEntryTypes';

interface TypeColor {
  id: string;
  color: string;
}

interface TimeOffEntryDetailContentProps {
  entry: TimeOffEntryViewModel;
  typeColors: TypeColor[];
  busyCancel?: boolean;
  busyDelete?: boolean;
  showOpenInAllTimeOff?: boolean;
  onCancel?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onClose: () => void;
  onOpenInAllTimeOff?: () => void;
}

export function TimeOffEntryDetailContent({
  entry,
  typeColors,
  busyCancel = false,
  busyDelete = false,
  showOpenInAllTimeOff = false,
  onCancel,
  onDelete,
  onEdit,
  onClose,
  onOpenInAllTimeOff
}: TimeOffEntryDetailContentProps) {
  const displayName = entry.needsEmployeeIdentity
    ? `⚠ Needs employee · ${entry.employeeDisplayName ?? entry.employeeUpn}`
    : entry.employeeDisplayName ?? entry.employeeUpn;
  const canCancel = entry.status !== 'Cancelled' && onCancel;
  const busy = busyCancel || busyDelete;
  const durationMode = inferDurationMode(new Date(entry.startUtc), new Date(entry.endUtc), entry.isAllDay);
  const durationSubtitle = durationModeLabel(durationMode);

  return (
    <div style={{ padding: '0.5rem 0.75rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ops-color-text, #111827)' }}>{displayName}</div>
        <div style={{ marginTop: 6, fontSize: 13, color: 'var(--ops-color-muted, #64748b)' }}>
          Email: {entry.employeeUpn}
        </div>
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span
            title="Leave type"
            style={{
              display: 'inline-block',
              padding: '0.2rem 0.65rem',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--ops-on-saturated-fg, #ffffff)',
              background: typeColors.find((t) => t.id === entry.type)?.color ?? 'var(--ops-color-muted, #64748b)'
            }}
          >
            {entry.type}
          </span>
          <span
            title="Approval status"
            style={{
              display: 'inline-block',
              padding: '0.2rem 0.65rem',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid var(--ops-border, #cbd5e1)',
              background: 'var(--ops-chip-neutral-bg, #ffffff)',
              color: 'var(--ops-color-text, #334155)'
            }}
          >
            {entry.status}
          </span>
        </div>
      </div>

      <div style={timeOffDetailSection}>
        <div style={timeOffDetailRowLabel}>When</div>
        <div style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--ops-color-text, #111827)' }}>
          {formatLocalRange(entry.startUtc, entry.endUtc, entry.isAllDay) || '—'}
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ops-color-muted, #64748b)' }}>{durationSubtitle}</div>
      </div>

      <div style={timeOffDetailSection}>
        <div style={timeOffDetailRowLabel}>Calendars</div>
        <div style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>
          {entry.calendarTargetNames.join(', ') || 'No calendars linked'}
        </div>
      </div>

      <div style={timeOffDetailSection}>
        <div style={timeOffDetailRowLabel}>Notes</div>
        <div
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: entry.notes ? 'var(--ops-color-text, #111827)' : 'var(--ops-color-muted, #94a3b8)'
          }}
        >
          {entry.notes?.trim() ? entry.notes : 'None'}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}
      >
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {canCancel ? (
            <Button
              text="Cancel time off"
              type="danger"
              stylingMode="outlined"
              disabled={busy}
              onClick={onCancel}
            />
          ) : null}
          {onDelete ? (
            <Button
              text="Delete"
              type="danger"
              stylingMode="contained"
              disabled={busy}
              onClick={onDelete}
            />
          ) : null}
          {onEdit ? (
            <Button
              text={busy ? 'Working…' : 'Edit'}
              icon="edit"
              stylingMode="contained"
              type="default"
              disabled={busy}
              onClick={onEdit}
            />
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {showOpenInAllTimeOff && onOpenInAllTimeOff ? (
            <Button
              stylingMode="text"
              text="Open in All Time Off"
              icon="chevronnext"
              onClick={onOpenInAllTimeOff}
              disabled={busy}
            />
          ) : null}
          <Button text="Close" onClick={onClose} disabled={busy} />
        </div>
      </div>
    </div>
  );
}
